import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { evaluarAlerta, notificacionDeAlerta } from '@/shared/domain/alertas';
import type { Alerta, ISODate, Notificacion } from '@/shared/domain/types';
import type { AppStoreLike } from '@/shared/store/listeners/types';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { updateAlertaLocal } from '@/shared/store/slices/alertasLocalSlice';
import { pushNotificacionLocal } from '@/shared/store/slices/notificacionesLocalSlice';
import { contextoDeAlertas, nuevoId } from './alertasContexto';
import { useUltimos } from './useMercado';

/** `fecha_dato` con la que se disparó la alerta por última vez (la saca de su última notificación). */
function ultimaFechaDato(alertaId: string, bandeja: readonly Notificacion[]): ISODate | null {
  const n = bandeja.find((x) => x.datos.alertaId === alertaId);
  const f = n?.datos.fechaDato;
  return typeof f === 'string' ? f : null;
}

function avisoDelSistema(titulo: string, cuerpo: string): void {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted')
      new Notification(titulo, { body: cuerpo });
  } catch {
    // Algunos navegadores móviles no permiten `new Notification`: la bandeja ya tiene el aviso.
  }
}

/**
 * Evalúa las alertas LOCALES cada vez que llegan precios nuevos y avisa (bandeja local y, si el usuario
 * dio permiso, notificación del navegador). Las alertas de la cuenta las evalúa el servidor.
 * Debe montarse una sola vez, en el layout de la app.
 */
export function useAlertasEvaluator(): void {
  const dispatch = useAppDispatch();
  const store = useStore() as AppStoreLike;
  const ultimos = useUltimos();
  const hayLocales = useAppSelector((s) => s.alertasLocal.items.length > 0);
  const evaluado = useRef<number | null>(null);

  useEffect(() => {
    const stamp = ultimos.updatedAt;
    if (!hayLocales || !ultimos.data || stamp === null || evaluado.current === stamp) return;
    evaluado.current = stamp;
    const datos = ultimos.data;
    void (async () => {
      const ahora = new Date();
      const activas = store.getState().alertasLocal.items.filter((a) => a.activa);
      const ctx = await contextoDeAlertas(activas, datos, ahora);
      if (!ctx) return;
      const lang = store.getState().lang.current;
      for (const alerta of activas) {
        const bandeja = store.getState().notificacionesLocal.items;
        const r = evaluarAlerta(alerta, {
          ...ctx,
          ultimaFechaDato: ultimaFechaDato(alerta.id, bandeja),
        });
        const iso = ahora.toISOString();
        const actualizada: Alerta = {
          ...alerta,
          ultimaEvaluacion: iso,
          ultimoValor: r.valor ?? alerta.ultimoValor,
          ...(r.dispara
            ? {
                ultimaDisparada: iso,
                estado: r.desactivar ? ('pausada' as const) : ('disparada' as const),
                activa: !r.desactivar,
              }
            : {}),
        };
        dispatch(updateAlertaLocal(actualizada));
        if (!r.dispara) continue;
        const nueva = notificacionDeAlerta(alerta, r, lang, iso);
        dispatch(pushNotificacionLocal({ ...nueva, id: nuevoId(), origen: 'local' }));
        avisoDelSistema(nueva.titulo, nueva.cuerpo);
      }
    })();
  }, [ultimos.data, ultimos.updatedAt, hayLocales, dispatch, store]);
}
